import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, ArrowLeft,
  Heart, Users, MessageCircle, Shield, Scale, Globe, Zap, Clock,
  Puzzle, Home, Lightbulb, Leaf, Smartphone, HeartCrack, Cloud,
  Mountain, Signpost, HandHeart, Quote, Handshake, Laptop, Target,
  Sparkles, Star, Compass, Flame, TreePine, TrendingUp, Fingerprint,
} from "lucide-react";
import slides from "../data/elternstimmeSlides";

/* ELTERNSTIMME palette — Dark Navy + Gold (premium, family-warm) */
const M = {
  navy: "#0E1A30",          // primary background
  navyDeep: "#081328",      // outer container
  navySoft: "#142545",      // card backgrounds in dark sections
  cream: "#FAF3E7",         // page (deck inner) background
  creamSoft: "#FFFBF0",     // card whites
  gold: "#D4AF37",          // primary accent
  goldSoft: "#E8C566",      // hover gold
  ink: "#0E1A30",           // primary text on cream
  inkSoft: "#3A4868",       // secondary text on cream
  whisper: "#7A85A0",       // tertiary
};

/* Rainbow gradient mapping (matches user's source design) */
const RAINBOW = ["#3D6BBF", "#D4AF37", "#5A9B7E", "#3D6BBF", "#7A85C4", "#C77B7B", "#0E1A30"];

const colorizeWord = (word, palette = RAINBOW) =>
  Array.from(word).map((ch, i) => (
    <span key={i} style={{ color: palette[i % palette.length] }}>{ch}</span>
  ));

/* Compass-Rose Brand-Mark (gold) */
const CompassMark = ({ size = 32, color = M.gold }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden>
    <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="1.2" opacity="0.55" />
    <g fill={color}>
      <path d="M32 6 L34.2 30 L32 32 L29.8 30 Z" />
      <path d="M32 58 L34.2 34 L32 32 L29.8 34 Z" opacity="0.7" />
      <path d="M6 32 L30 29.8 L32 32 L30 34.2 Z" opacity="0.7" />
      <path d="M58 32 L34 29.8 L32 32 L34 34.2 Z" opacity="0.7" />
    </g>
    <circle cx="32" cy="32" r="2" fill={color} />
  </svg>
);

/* Icon map */
const ICONS = {
  users: Users, speech: MessageCircle, shield: Shield, balance: Scale,
  globe: Globe, lightning: Zap, clock: Clock, "clock-fast": Clock,
  heart: Heart, puzzle: Puzzle, home: Home, bulb: Lightbulb, leaf: Leaf,
  phone: Smartphone, "heart-broken": HeartCrack, cloud: Cloud,
  mountain: Mountain, signpost: Signpost, hug: HandHeart,
  "high-five": Handshake, "hand-heart": HandHeart, "speech-group": MessageCircle,
  laptop: Laptop, target: Target, meditate: Sparkles, star: Star,
  compass: Compass, candle: Flame, tree: TreePine, trending: TrendingUp,
  family: Users, handprints: Fingerprint, fingerheart: Fingerprint,
  "users-glow": Users, "fingerheart-green": Fingerprint, "heart-glow": Heart,
};
const I = (key, props) => {
  const Cmp = ICONS[key] || Heart;
  return <Cmp {...props} />;
};

/* Decorative dotted gold separator */
const GoldDivider = () => (
  <div className="flex items-center justify-center gap-1.5 my-3">
    <span className="block w-12 h-px" style={{ background: `linear-gradient(90deg, transparent, ${M.gold})` }} />
    <Heart size={10} style={{ color: M.gold, fill: M.gold }} />
    <span className="block w-12 h-px" style={{ background: `linear-gradient(90deg, ${M.gold}, transparent)` }} />
  </div>
);

/* Intro paragraph: supports plain strings or { html } objects */
const renderIntro = (intro) => {
  if (!intro) return null;
  const arr = Array.isArray(intro) ? intro : [intro];
  return arr.map((p, i) =>
    typeof p === "object" && p?.html ? (
      <p
        key={i}
        className="text-sm md:text-base leading-relaxed mb-2.5"
        style={{ color: M.inkSoft }}
        dangerouslySetInnerHTML={{ __html: p.html }}
      />
    ) : (
      <p key={i} className="text-sm md:text-base leading-relaxed mb-2.5" style={{ color: M.inkSoft }}>
        {p}
      </p>
    ),
  );
};

/* ── COVER ─────────────────────────────────────────────────────────────── */
const Cover = ({ s }) => (
  <div className="w-full px-8 lg:px-12 pt-8 pb-10" style={{ color: M.ink }}>
    <div className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-3" style={{ color: M.ink }}>
      HERZ. VERSTAND. SEELE. KRAFT. ALLE.
    </div>
    <GoldDivider />

    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-10 items-start mt-2">
      {/* LEFT */}
      <div>
        <h1 className="serif font-light leading-[1] tracking-tight mb-4" style={{ fontSize: "clamp(2rem,4vw,3.2rem)" }}>
          <div style={{ color: M.navy }}>{s.title.replace("™", "")}<sup className="text-base align-super ml-0.5" style={{ color: M.gold }}>™</sup></div>
          {s.headline?.map((h, i) => {
            let inner;
            if (h.c === "rainbow") inner = colorizeWord(h.t);
            else if (h.c === "gold") inner = <span style={{ color: M.gold }}>{h.t}</span>;
            else inner = <span style={{ color: M.navy }}>{h.t}</span>;
            return (
              <div key={i} className="leading-[1.05]" style={{ fontSize: "clamp(1.6rem,3.4vw,2.8rem)" }}>
                {inner}
              </div>
            );
          })}
        </h1>

        <GoldDivider />

        <div className="mt-4 max-w-xl">{renderIntro(s.intro)}</div>

        {/* Pillars row */}
        {s.pillars && (
          <div className="mt-6 grid grid-cols-5 gap-2 max-w-xl">
            {s.pillars.map((p, i) => (
              <div key={i} className="text-center">
                <div
                  className="mx-auto w-11 h-11 rounded-full flex items-center justify-center border-2"
                  style={{ borderColor: `${p.color}55`, background: `${p.color}10` }}
                >
                  {I(p.icon, { size: 18, style: { color: p.color } })}
                </div>
                <div
                  className="mt-1.5 font-bold text-[8px] tracking-[0.08em] leading-tight"
                  style={{ color: M.ink }}
                >
                  {p.h}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT — quote box */}
      <div className="flex flex-col gap-4">
        {s.quote && (
          <div
            className="rounded-2xl p-5 relative"
            style={{ background: M.navy, color: M.cream }}
          >
            <Quote size={18} className="absolute top-3 left-3 opacity-50" style={{ color: M.gold }} />
            <div className="pl-6 pt-1">
              <p className="serif italic text-sm leading-relaxed">{s.quote}</p>
              <div className="mt-3 flex justify-end">
                <Heart size={14} style={{ color: M.gold, fill: M.gold }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Bottom mission banner */}
    {s.mission && (
      <div
        className="mt-8 rounded-2xl p-5 flex items-center gap-5"
        style={{ background: M.navy, color: M.cream }}
      >
        <div className="shrink-0 relative">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 70%)" }}
          >
            <Heart size={28} style={{ color: M.gold, fill: M.gold }} />
          </div>
        </div>
        <p className="text-xs md:text-sm leading-relaxed flex-1" style={{ letterSpacing: "0.04em" }}>
          {s.mission}
        </p>
        <div className="shrink-0 hidden md:flex items-center gap-2 opacity-80" style={{ color: M.gold }}>
          <Users size={18} /> <Heart size={16} fill={M.gold} /> <Globe size={18} />
        </div>
      </div>
    )}
  </div>
);

/* ── CONTENT (6-card grid or 5 numbered or 8 dark values or duoPanels or legacy) ── */
const QuoteBox = ({ s }) => {
  if (!s.quote) return null;
  const light = s.quoteVariant === "light";
  return (
    <div
      className="rounded-2xl p-5 relative"
      style={{
        background: light ? "#FFFBF0" : M.navy,
        color: light ? M.ink : M.cream,
        border: light ? `1px solid ${M.gold}55` : "none",
      }}
    >
      <Quote size={18} className="absolute top-3 left-3 opacity-50" style={{ color: M.gold }} />
      <div className="pl-6 pt-1">
        <p className="serif italic text-sm leading-relaxed">{s.quote}</p>
        <div className="mt-3 flex justify-end">
          <Heart size={14} style={{ color: M.gold, fill: M.gold }} />
        </div>
      </div>
    </div>
  );
};

const Banner = ({ s }) => {
  if (!s.bottomBanner) return null;
  const cream = s.bottomBannerVariant === "cream";
  return (
    <div
      className="mt-5 rounded-2xl p-4 md:p-5 flex items-center gap-4"
      style={{
        background: cream ? "#FFFBF0" : M.navy,
        color: cream ? M.ink : M.cream,
        border: cream ? `1px solid ${M.gold}55` : "none",
      }}
    >
      <div className="shrink-0">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 70%)" }}
        >
          <Heart size={24} style={{ color: M.gold, fill: M.gold }} />
        </div>
      </div>
      <p className="text-[11px] md:text-sm leading-relaxed flex-1" style={{ letterSpacing: "0.04em" }}>
        {s.bottomBanner}
      </p>
      <div className="shrink-0 hidden md:flex items-center gap-2 opacity-70" style={{ color: M.gold }}>
        <Users size={16} />
      </div>
    </div>
  );
};

/* Legacy / multi-column footer panels */
const LegacyPanels = ({ panels }) => {
  if (!panels?.length) return null;
  const bg = (kind) => {
    if (kind === "cream") return { background: "#FFFBF0", borderColor: `${M.gold}55` };
    if (kind === "softblue") return { background: "#EAF1FA", borderColor: "#3D6BBF33" };
    if (kind === "rose") return { background: "#FBEAEE", borderColor: "#C77B7B55" };
    return { background: "#FFFFFF", borderColor: "#E0E0E0" };
  };
  const cols = panels.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3";
  return (
    <div className={`mt-4 grid grid-cols-1 ${cols} gap-3`}>
      {panels.map((p, i) => {
        const style = bg(p.kind);
        return (
          <div
            key={i}
            className="rounded-2xl border p-4 flex gap-3"
            style={{ background: style.background, borderColor: style.borderColor }}
          >
            {p.icon && (
              <div className="shrink-0">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: `${M.gold}18` }}
                >
                  {I(p.icon, { size: 22, style: { color: M.gold } })}
                </div>
              </div>
            )}
            <div className="flex-1">
              {p.title && (
                <div className="font-bold text-[11px] tracking-[0.18em] uppercase mb-1.5" style={{ color: M.navy }}>
                  {p.title}
                </div>
              )}
              {p.text && (
                <p className="text-[12px] leading-snug mb-1" style={{ color: M.inkSoft }}>
                  {p.text}
                </p>
              )}
              {p.list && (
                <ul className="space-y-1">
                  {p.list.map((it, j) => (
                    <li key={j} className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: M.inkSoft }}>
                      <span style={{ color: M.gold }}>✓</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
              {p.emphasis && (
                <div className="mt-1.5 text-[12px] font-bold" style={{ color: "#C77B7B" }}>
                  {p.emphasis}
                </div>
              )}
              {p.footer && (
                <div className="mt-2 text-[10px] font-bold tracking-wide uppercase" style={{ color: M.gold }}>
                  {p.footer}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* Two panels (slide 10) — supports dark + light variants */
const DuoPanels = ({ panels }) => {
  if (!panels?.length) return null;
  return (
    <div className="mt-4 grid md:grid-cols-2 gap-3">
      {panels.map((p, i) => {
        const light = p.variant === "light-color";
        return (
          <div
            key={i}
            className="rounded-2xl p-4 border"
            style={
              light
                ? { background: "#FFFBF0", borderColor: `${M.gold}55`, color: M.ink }
                : { background: "linear-gradient(180deg, #142545 0%, #0E1A30 100%)", borderColor: `${M.gold}55`, color: M.cream }
            }
          >
            <div
              className="inline-block rounded-md px-4 py-1.5 mb-3 text-[11px] tracking-[0.32em] uppercase font-bold"
              style={{ background: M.navy, color: M.cream }}
            >
              {p.title}
            </div>
            <div className="space-y-3">
              {p.items.map((it, j) => (
                <div key={j} className="flex items-start gap-3">
                  <div className="shrink-0">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ background: it.color }}
                    >
                      {I(it.icon, { size: 16, style: { color: "#FFFFFF" } })}
                    </div>
                  </div>
                  <p
                    className="text-[12px] leading-snug pt-1"
                    style={{ color: light ? M.inkSoft : "rgba(250,243,231,0.88)" }}
                    dangerouslySetInnerHTML={{ __html: it.text }}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Content = ({ s }) => {
  const isDuoPanels = s.layout === "duoPanels";
  const isLegacy = ["valuesLegacy", "legacy", "promises", "voice"].includes(s.layout);
  const isNumbered = !!s.numberedCards;
  const cards = s.cards || s.numberedCards || s.eightCards || [];
  const hasMultiTitle = Array.isArray(s.titleLines);

  return (
    <div className="w-full px-8 lg:px-12 pt-7 pb-8" style={{ color: M.ink }}>
      <div className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-2" style={{ color: M.ink }}>
        HERZ. VERSTAND. SEELE. KRAFT. ALLE.
      </div>
      <GoldDivider />

      <div className="grid lg:grid-cols-[1.45fr_1fr] gap-6 lg:gap-10 mt-2 items-start">
        {/* LEFT — title + intro */}
        <div>
          <h2 className="serif font-light leading-[1.05] mb-1" style={{ fontSize: "clamp(1.6rem,3vw,2.6rem)" }}>
            <div style={{ color: M.navy }}>{s.kicker}</div>
            {hasMultiTitle ? (
              s.titleLines.map((line, i) => (
                <div
                  key={i}
                  className="mt-1"
                  style={{ color: s.titleGold?.includes(line) ? M.gold : M.navy }}
                >
                  {line}
                </div>
              ))
            ) : (
              <div className="mt-1" style={{ color: M.navy }}>
                {colorizeWord(s.title)}
              </div>
            )}
          </h2>
          <GoldDivider />
          <div className="mt-2 max-w-xl">{renderIntro(s.intro)}</div>

          {s.motto && (
            <div className="mt-4">
              <div
                className="serif font-semibold text-sm md:text-base leading-snug"
                style={{ color: M.navy, letterSpacing: "0.02em" }}
              >
                {colorizeWord(s.motto)}
              </div>
              {s.mottoSub && (
                <div className="text-xs md:text-sm mt-1" style={{ color: M.inkSoft }}>
                  {s.mottoSub}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — quote box */}
        <div>
          <QuoteBox s={s} />
        </div>
      </div>

      {/* DuoPanels variant (slide 10) — no card grid */}
      {isDuoPanels && <DuoPanels panels={s.duoPanels} />}

      {/* Cards section title */}
      {s.cardsTitle && !isDuoPanels && (
        <div className="mt-5">
          <div
            className="inline-block rounded-md px-4 py-1.5 mb-3 text-[11px] tracking-[0.25em] font-bold uppercase"
            style={{ background: M.navy, color: M.cream }}
          >
            {s.cardsTitle}
          </div>
        </div>
      )}

      {/* LIGHT CARDS variant (5 / 6 / 8) */}
      {!isDuoPanels && cards.length > 0 && (
        <div
          className={`grid gap-3 ${
            s.eightCards
              ? "grid-cols-2 md:grid-cols-4 lg:grid-cols-8"
              : isNumbered && cards.length === 5
                ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-6"
          }`}
        >
          {cards.map((c, i) => (
            <div
              key={i}
              className="rounded-2xl p-3 border bg-white relative overflow-hidden"
              style={{ borderColor: `${c.color}33` }}
            >
              {isNumbered && (
                <div
                  className="absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs text-white"
                  style={{ background: c.color }}
                >
                  {c.n}
                </div>
              )}
              <div className={`flex items-start ${isNumbered ? "justify-end" : s.eightCards ? "justify-center" : "justify-start"} mb-2`}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={c.solid
                    ? { background: c.color }
                    : { background: `${c.color}15`, borderRadius: "0.75rem" }}
                >
                  {I(c.icon, { size: 20, style: { color: c.solid ? "#FFFFFF" : c.color } })}
                </div>
              </div>
              <div
                className={`font-bold text-[10.5px] leading-tight tracking-wide uppercase mb-1 ${s.eightCards ? "text-center" : ""}`}
                style={{ color: c.color }}
              >
                {c.h}
              </div>
              <div
                className={`text-[10.5px] leading-snug ${s.eightCards ? "text-center" : ""}`}
                style={{ color: M.inkSoft }}
              >
                {c.t}
              </div>
              <div
                className="absolute bottom-0 left-3 right-3 h-[2.5px] rounded-full"
                style={{ background: c.color, opacity: 0.85 }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Legacy multi-column panels (slides 11–15) */}
      {isLegacy && <LegacyPanels panels={s.legacyPanels} />}

      {/* Bottom banner */}
      <Banner s={s} />
    </div>
  );
};

/* ── FINAL CTA (slide 16) — cinematic closer ────────────────────────────── */
const FinalCTA = ({ s }) => (
  <div className="w-full px-8 lg:px-12 pt-7 pb-8" style={{ color: M.ink }}>
    <div className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-2" style={{ color: M.ink }}>
      HERZ. VERSTAND. SEELE. KRAFT. ALLE.
    </div>
    <GoldDivider />

    <div className="grid lg:grid-cols-[1.55fr_1fr] gap-6 lg:gap-10 mt-2 items-start">
      <div>
        <h2 className="serif font-light leading-[1.05] mb-1" style={{ fontSize: "clamp(1.6rem,3vw,2.6rem)" }}>
          <div style={{ color: M.navy }}>{s.kicker}</div>
          <div className="mt-1" style={{ color: M.navy }}>
            {colorizeWord(s.title)}
          </div>
        </h2>
        <GoldDivider />
        {s.subtitle && (
          <p className="serif italic mt-2 mb-3" style={{ color: M.gold, fontSize: "clamp(1rem,1.4vw,1.25rem)" }}>
            {s.subtitle}
          </p>
        )}
        <div className="mt-2 max-w-xl">{renderIntro(s.intro)}</div>
        {s.emphasis && (
          <p className="mt-2 font-semibold text-sm md:text-base" style={{ color: M.navy }}>
            {colorizeWord(s.emphasis)}
          </p>
        )}

        {/* Heart icon row */}
        {s.heartIcons && (
          <div className="mt-5 grid grid-cols-5 gap-3 max-w-xl">
            {s.heartIcons.map((h, i) => (
              <div key={i} className="text-center">
                <div className="relative mx-auto w-14 h-14 flex items-center justify-center">
                  <Heart size={48} style={{ color: h.color, fill: h.color }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    {I(h.icon, { size: 18, style: { color: "#FFFFFF" } })}
                  </div>
                </div>
                <div className="mt-1.5 text-[9px] font-bold tracking-[0.1em]" style={{ color: h.color }}>
                  {h.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <QuoteBox s={s} />
      </div>
    </div>

    {/* Action steps */}
    {s.actions && (
      <div className="mt-6 rounded-2xl border p-4" style={{ background: "#FFFBF0", borderColor: `${M.gold}55` }}>
        <div
          className="inline-block rounded-md px-4 py-1.5 mb-3 text-[11px] tracking-[0.25em] font-bold uppercase"
          style={{ background: M.navy, color: M.cream }}
        >
          {s.actionsTitle}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {s.actions.map((a, i) => (
            <div
              key={i}
              className="rounded-xl border p-3 bg-white text-center"
              style={{ borderColor: `${a.color}55` }}
            >
              <div
                className="mx-auto w-9 h-9 rounded-full flex items-center justify-center mb-2"
                style={{ background: a.color }}
              >
                {I("heart", { size: 14, style: { color: "#FFFFFF", fill: "#FFFFFF" } })}
              </div>
              <div className="text-[11px] leading-snug font-semibold" style={{ color: M.navy }}>
                {a.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Movement Box (Herzbewegung CTA) */}
    {s.movementBox && (
      <div
        className="mt-4 rounded-2xl border p-5 flex items-center gap-5"
        style={{ background: "#FFFBF0", borderColor: `${M.gold}55` }}
      >
        <div className="shrink-0">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: "radial-gradient(circle, rgba(199,123,123,0.35) 0%, transparent 70%)" }}
          >
            <Heart size={40} style={{ color: "#C77B7B", fill: "#C77B7B" }} />
          </div>
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm md:text-base tracking-wide uppercase" style={{ color: "#C77B7B" }}>
            {s.movementBox.title}
          </div>
          <p className="text-[12px] md:text-sm mt-0.5" style={{ color: M.inkSoft }}>
            {s.movementBox.sub}
          </p>
        </div>
        <Link
          to={s.movementBox.to}
          className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition hover:translate-x-0.5"
          style={{ background: M.gold, color: M.navy }}
          data-testid="elternstimme-finalcta-herzbewegung"
        >
          {s.movementBox.cta}
        </Link>
      </div>
    )}

    {/* Closing line */}
    {s.closing && (
      <div className="mt-6 text-center">
        <div className="serif italic" style={{ color: M.gold, fontSize: "clamp(1.4rem,2.2vw,2rem)" }}>
          {s.closing}
        </div>
        {s.closingSub && (
          <div className="mt-1 text-[11px] tracking-[0.3em] uppercase font-semibold" style={{ color: M.navy }}>
            {s.closingSub}
          </div>
        )}
      </div>
    )}
  </div>
);

const render = (s) => {
  if (s.type === "cover") return <Cover s={s} />;
  if (s.type === "finalCTA") return <FinalCTA s={s} />;
  return <Content s={s} />;
};

export default function SatElternstimmeDeck() {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const total = slides.length;
  const go = useCallback((d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))), [total]);

  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "f") setFs((f) => !f);
      else if (e.key === "Escape") setFs(false);
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(total - 1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, total]);

  const slide = slides[idx];

  return (
    <div
      className={`${fs ? "fixed inset-0 z-[100]" : "min-h-screen pt-24 pb-12"}`}
      style={{ background: M.navyDeep }}
      data-testid="sat-elternstimme-deck"
    >
      <div className={`${fs ? "h-screen" : ""} container-sat`}>
        {!fs && (
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/akademie/decks"
              className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase transition hover:translate-x-[-2px]"
              style={{ color: M.gold }}
              data-testid="elternstimme-deck-back"
            >
              <ArrowLeft size={12} /> Zurück
            </Link>
            <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: "rgba(255,255,255,0.5)" }}>
              ELTERNSTIMME · Initiative der SAT Akademie
            </div>
            <button
              onClick={() => setFs(true)}
              className="flex items-center gap-2 text-xs uppercase tracking-wider transition"
              style={{ color: M.gold }}
              data-testid="elternstimme-deck-fullscreen-on"
            >
              <Maximize2 size={14} /> Vollbild
            </button>
          </div>
        )}

        <div
          className={`relative ${fs ? "h-full" : "min-h-[680px]"} rounded-2xl overflow-hidden`}
          style={{
            background: `linear-gradient(135deg, ${M.cream} 0%, ${M.creamSoft} 60%, ${M.cream} 100%)`,
            border: fs ? "none" : `1px solid ${M.gold}44`,
          }}
          data-testid={`elternstimme-slide-${slide.num}`}
        >
          {/* LEFT brand strip (dark navy) */}
          <div
            className="absolute top-0 bottom-0 left-0 w-[80px] lg:w-[120px] flex flex-col items-center py-8 z-10"
            style={{ background: M.navy, color: M.cream }}
          >
            <CompassMark size={42} color={M.gold} />
            <div className="mt-4 text-center leading-tight">
              <div className="serif text-lg font-light tracking-wider" style={{ color: M.cream }}>SAT</div>
              <div className="text-[7px] tracking-[0.25em] uppercase mt-1 px-2 opacity-80" style={{ color: M.cream }}>
                Schweizer Akademie
              </div>
              <div className="text-[7px] tracking-[0.25em] uppercase opacity-80" style={{ color: M.cream }}>
                für Transformation
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1 opacity-90">
              <span className="text-[8px]" style={{ color: M.cream }}>🇨🇭</span>
              <Heart size={10} style={{ color: M.gold, fill: M.gold }} />
            </div>

            <div
              className="hidden lg:block mt-5 text-center px-2"
              style={{ fontFamily: "Georgia, serif", color: M.goldSoft, fontStyle: "italic" }}
            >
              <div className="text-sm">Nenad</div>
              <div className="text-sm">Vladović</div>
              <div className="text-[8px] tracking-[0.18em] uppercase mt-2 opacity-80" style={{ color: M.cream }}>
                Gründer der SAT Akademie
              </div>
              <div className="text-[8px] tracking-[0.18em] uppercase opacity-80" style={{ color: M.cream }}>
                für Transformation
              </div>
            </div>

            <div className="mt-auto pb-2 text-center text-[8px] tracking-[0.22em] uppercase leading-relaxed hidden lg:block px-1">
              <div style={{ color: "#3D6BBF" }}>Deine Stimme.</div>
              <div className="opacity-70" style={{ color: M.cream }}>Deine Selbstverantwortung.</div>
              <div style={{ color: "#5A9B7E" }}>Deine Kraft.</div>
              <div style={{ color: M.gold }}>Deine Integrität.</div>
              <div style={{ color: "#C77B7B" }}>Dein Erbe.</div>
            </div>

            {/* Page badge bottom */}
            <div className="mt-2 mb-1">
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center border"
                style={{ borderColor: M.gold, color: M.gold }}
              >
                <span className="text-sm font-light">{slide.num}</span>
              </div>
            </div>
          </div>

          {/* Page counter top-right */}
          <div
            className="absolute top-5 right-6 text-xs tracking-[0.3em] uppercase font-light z-10"
            style={{ color: M.ink }}
          >
            {String(slide.num).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>

          {/* Slide body — offset for left strip */}
          <div className="pl-[80px] lg:pl-[120px]">
            <div>{render(slide)}</div>
          </div>

          {/* Footer */}
          <div
            className="absolute bottom-0 left-[80px] lg:left-[120px] right-0 text-center text-[9px] tracking-[0.3em] uppercase py-2"
            style={{ background: M.navy, color: M.cream }}
          >
            ELTERNSTIMME™ — Eine Initiative der SAT Akademie für Transformation 🇨🇭
          </div>

          {fs && (
            <button
              onClick={() => setFs(false)}
              className="absolute top-5 right-1/2 translate-x-1/2 flex items-center gap-2 text-xs uppercase tracking-wider z-20"
              style={{ color: M.ink }}
              data-testid="elternstimme-deck-fullscreen-off"
            >
              <Minimize2 size={14} /> Schließen (Esc)
            </button>
          )}
        </div>

        {/* Controls */}
        <div className="mt-5 flex items-center justify-between">
          <button
            onClick={() => go(-1)}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm transition disabled:opacity-30"
            style={{ borderColor: `${M.gold}55`, color: M.gold }}
            data-testid="elternstimme-deck-prev"
          >
            <ChevronLeft size={16} /> Vorherige
          </button>
          <div className="hidden md:flex items-center gap-1.5 max-w-2xl overflow-x-auto">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className="shrink-0 transition-all rounded-full"
                style={{
                  width: i === idx ? "24px" : "8px",
                  height: "8px",
                  background: i === idx ? M.gold : "rgba(212,175,55,0.3)",
                }}
                aria-label={`Slide ${i + 1}`}
                data-testid={`elternstimme-deck-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            disabled={idx === total - 1}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition disabled:opacity-30"
            style={{ background: M.gold, color: M.navy }}
            data-testid="elternstimme-deck-next"
          >
            Nächste <ChevronRight size={16} />
          </button>
        </div>

        {!fs && (
          <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase" style={{ color: "rgba(255,255,255,0.4)" }}>
            Pfeiltasten ◄ ► · Leertaste · F für Vollbild · Esc zum Schließen
          </div>
        )}
      </div>
    </div>
  );
}
